import React, { useEffect, useState } from 'react';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import _ from 'underscore';
import { DefaultButton, Stack } from '@fluentui/react';
import { AdPlacementCardPreview } from '@bingads-webui-campaign-react/ad-placement-preview';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { IImageExperienceImage } from '@bingads-webui-campaign-react/responsive-ad-editor-utils';
import AdRecommendRepository from '../utils/ad-recommend-repository';
import { getRecommendCacheByUrl, setRecommendCache } from './recommend-ad-cache';
import { IActivityLogger, IRecommendAd, IAdRecommendResponse, ISetUpAdWithRecommendAdParam } from './types';
import { convertAdUnitsToImageExperiences } from '../utils/ad-recommend-util';

declare let _TL_: any;

interface IProps {
  url: string;
  baseURL: string;
  onSaveRecommendAd: () => void;
  setUpAdWithRecommendAd: (param: ISetUpAdWithRecommendAdParam) => void;
  i18n: any;
  i18nErrors: any;
  errorCodes: string[];
  activityLogger: IActivityLogger;
}

export function RecommendAdList(props: IProps) {
  const {
    url, baseURL, i18n, setUpAdWithRecommendAd, errorCodes, i18nErrors, activityLogger,
  } = props;

  const [loaded, setLoaded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [ads, setAds] = useState<IRecommendAd[]>([]);
  const [imageExperiences, setImageExperiences] = useState<IImageExperienceImage[]>([]);

  useEffect(() => {
    const fetchRecommendation = async () => {
      if (url) {
        const repository = new AdRecommendRepository({ baseURL });

        const resultFromCache = getRecommendCacheByUrl(url);

        try {
          let res: IAdRecommendResponse;
          if (resultFromCache) {
            res = resultFromCache;
          } else {
            res = await repository.getRecommendAds(url);
            activityLogger.signal(`fetch ad recommendation. The final URL: ${url}. The Response: ${JSON.stringify(res)}`, 'responsive ad recommendation');
          }
          setRecommendCache(url, res);

          const result = convertAdUnitsToImageExperiences(res.value, { imageListIndex: 0 });

          setAds(res.value);
          setImageExperiences(result);
          setLoaded(true);
        } catch (error) {
          setLoaded(true);
        }
      }
    };

    fetchRecommendation();
  }, [activityLogger, baseURL, url]);

  useEffect(() => {
    setClarityTag({ AdRecommendationMSANList: 'true' });
    activityLogger.signal(`show the recommendation list - recommend-ad-list. The final URL: ${url}.`, 'responsive ad recommendation');
  }, [activityLogger, url]);

  async function onEdit(e) {
    activityLogger.signal(`Edit - recommend-ad-list. The final URL: ${url}.`, 'responsive ad recommendation');
    setCreating(true);
    await setUpAdWithRecommendAd({
      ad: ads[e.currentTarget.id],
      isAdEditShown: true,
      shouldSaveAdFromRecommend: false,
    });
    setCreating(false);
  }

  async function onApply(e) {
    activityLogger.signal(`Apply - recommend-ad-list. The final URL: ${url}.`, 'responsive ad recommendation');
    setCreating(true);
    await setUpAdWithRecommendAd({
      ad: ads[e.currentTarget.id],
      isAdEditShown: false,
      shouldSaveAdFromRecommend: true,
    });
    setCreating(false);
  }

  return imageExperiences.length === 0 ? null : (
    <div className="recommend-ad-list">
      <UiBlocker
        blocking={!loaded || creating}
      >
        <div className="recommend-title">{i18n.getString(_TL_('Recommended Ads for this Campaign'))}</div>
        <div className="ad-recommend-list-container" >
          <Stack horizontal tokens={{ childrenGap: 20 }}>
            {imageExperiences.map((item, index) => {
              const {
                Headline,
                LongHeadline,
                Text,
                BusinessName,
              } = ads[index];
              const { aspectRatio, imagePreviewHeight } = constants.aspectRatios.Image191x100;
              return (
                <div className="recommend-ad-card">
                  <AdPlacementCardPreview
                    image={item}
                    additionalSetting={{
                      aspectRatio,
                      imagePreviewHeight,
                    }}
                    previewTexts={{
                      longHeadline: LongHeadline,
                      shortHeadline: Headline,
                      description: Text,
                      businessName: BusinessName,
                      url,
                    }}
                    i18n={i18n}
                    {..._.get(constants.aspectRatios.Image191x100, ['previewSettings'], {})}
                  />

                  <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <DefaultButton
                      id={String(index)}
                      onClick={onEdit}
                      className="edit-ad"
                      text={i18n.getString(_TL_('Edit'))}
                    />
                    <DefaultButton
                      id={String(index)}
                      onClick={onApply}
                      className="apply-ad"
                      text={i18n.getString(_TL_('Apply'))}
                    />
                  </Stack>
                </div>
              );
            })}
          </Stack>
        </div>
        <div className="ad-recommend-save-errors">
          {errorCodes.map((errorCode: string) => (
            <div key={errorCode}>
              {i18nErrors.getString(`API_ErrorNumber_${errorCode}`) || i18nErrors.getString('ErrorNumber_-1')}
            </div>
            ))}
        </div>
      </UiBlocker>
    </div>
  );
}
